import {
  ComponentProps,
  FC,
  memo,
  MouseEventHandler,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
// import { useDispatch } from 'react-redux';
import cc from 'classcat';
import { DEBOUNCE_DELAY } from 'utils/constants/app/magic-numbers-app';
import { divideArray } from 'utils/generic/divide-array';
import { lazy } from 'utils/generic/lazy';
import { isMobile } from 'utils/is-mobile';
import { NewCasinoGame } from 'interfaces/new-casino';
import { SeeAllGames } from './SeeAllGames';
import { CasinoGameCard as CasinoGameCardComponent } from 'newcomponents/Shared/casino/CasinoGameCard';
import { CasinoGameCardSkeleton } from 'newcomponents/Shared/casino/CasinoGameCard/Skeleton';
// import { useClickOutside } from 'hooks/useClickOutside';
import { useDebouncedCallback } from 'hooks/useDebouncedCallback';
import { useInView } from 'newhooks/dom/useInView';
// import { setMobileActiveCardID } from 'store/actions/new-casino';
import { useGamesLoadMore } from '../../hooks/useGamesLoadMore';
import { useCasinoGamesConfigsValues } from '../../providers/CasinoGamesConfigsContext';
import { CasinoGamesByCategorySkeleton } from '../GamesByCategory/Skeleton';
import './index.less';

const { SwiperSlider } = lazy(
  () =>
    import(
      /* webpackChunkName: "swiper-wrapper" */ 'newcomponents/UI/SwiperSlider'
    )
);

interface ICasinoGamesCarouselProps {
  loadMoreFunctionalityAvailable: boolean;
  data: NewCasinoGame[];
  viewAllEnabled: boolean;
  onViewAll: MouseEventHandler<HTMLDivElement>;
  CasinoGameCard: (
    props: Pick<
      ComponentProps<typeof CasinoGameCardComponent>,
      'game' | 'index'
    >
  ) => ReturnType<typeof CasinoGameCardComponent>;
  gamesCount: number;
}

const _CasinoGamesCarousel: FC<ICasinoGamesCarouselProps> = ({
  data,
  loadMoreFunctionalityAvailable,
  CasinoGameCard,
  viewAllEnabled,
  onViewAll,
  gamesCount
}) => {
  const { computedConfigs } = useCasinoGamesConfigsValues();
  const { handleLoadMore } = useGamesLoadMore();
  const [observableReference, setObservableReference] =
    useState<HTMLDivElement | null>();

  const scrollableReference = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  // const dispatch = useDispatch();

  // const setMobileActiveCardCLickHandler = useCallback(
  //   (id: null | string = null) => {
  //     isMobile() && dispatch(setMobileActiveCardID(id));
  //   },
  //   []
  // );
  //
  // useClickOutside(containerRef, () => {
  //   isMobile() && setMobileActiveCardCLickHandler(null);
  // });
  const inView = useInView(observableReference, {
    destructOnFirstIntersect: true,
    options: {
      threshold: 0.5
    }
  });

  // this technique ensures that the observableReference is attached at least to the last card based on
  // games rows and games per row to be visible in case of user swipes very quickly
  const observableIndexDiff =
    computedConfigs.gamesPerRow * computedConfigs.gamesRows;

  const showArrows = isMobile() ? computedConfigs.arrowsEnabled : true;

  const loadMoreGames = useCallback(() => {
    if (!loadMoreFunctionalityAvailable) {
      return false;
    }

    handleLoadMore();

    return true;
  }, [loadMoreFunctionalityAvailable, handleLoadMore]);

  const debouncedLoadMoreGames = useDebouncedCallback(
    loadMoreGames,
    DEBOUNCE_DELAY._300
  );

  // the check when arrows are disabled is done through attaching the ref to game card
  useEffect(() => {
    if (!inView) {
      return;
    }

    loadMoreGames();
  }, [inView]);

  // the check when arrows are disabled is done through attaching the ref to scrollable element
  useEffect(() => {
    const scrollableElement = scrollableReference.current;

    if (!scrollableElement) {
      return;
    }

    let loadMoreCalled = false;

    const handleScroll = () => {
      if (!scrollableElement) {
        return;
      }

      const totalScrollWidth = scrollableElement.scrollWidth;
      const scrollLeft = scrollableElement.scrollLeft;
      const clientWidth = scrollableElement.clientWidth;

      if (
        scrollLeft + clientWidth + clientWidth > totalScrollWidth &&
        !loadMoreCalled
      ) {
        loadMoreCalled = loadMoreGames();
      }
    };

    scrollableElement.addEventListener('scroll', handleScroll);

    return () => {
      if (!scrollableElement) {
        return;
      }

      scrollableElement.removeEventListener('scroll', handleScroll);
    };
  }, [data.length]);

  const drawableData = useMemo(() => {
    let res;

    if (computedConfigs.gamesRows > 1) {
      const dividedData = divideArray(
        data,
        computedConfigs.gamesRows,
        data.length
      );

      res = dividedData.map((group, groupIndex, dataArray) => (
        <div
          className="casinoGame__carousel-item casinoGame__carousel-item-grid"
          key={group[0].id}
          /* this ensures that observable reference is being attached only when arrows are enabled*/
          {...(showArrows &&
            groupIndex + observableIndexDiff === dataArray.length && {
              ref: setObservableReference
            })}
        >
          {group.map(
            (game, index) =>
              game && (
                <div
                  key={game.id}
                  // onClick={() => {
                  //   setMobileActiveCardCLickHandler(game.id);
                  // }}
                >
                  <CasinoGameCard game={game} index={index} key={game.id} />
                </div>
              )
          )}
        </div>
      ));
    } else {
      res = data.map((game, index, dataArray) => (
        <div
          className="casinoGame__carousel-item"
          key={game.id}
          // onClick={() => {
          //   setMobileActiveCardCLickHandler(game.id);
          // }}
          /* this ensures that observable reference is being attached only when arrows are enabled*/
          {...(showArrows &&
            index + observableIndexDiff === dataArray.length && {
              ref: setObservableReference
            })}
        >
          <CasinoGameCard game={game} index={index} />
        </div>
      ));
    }

    if (!viewAllEnabled && isMobile() && data.length < gamesCount) {
      res.push(
        <div className="casinoGame__carousel-item casinoGame__carousel-item-grid">
          <CasinoGameCardSkeleton />
        </div>
      );
    }

    if (viewAllEnabled && isMobile()) {
      res.push(
        <div className="casinoGame__carousel-item casinoGame__carousel-item-grid">
          <SeeAllGames
            onViewAll={onViewAll}
            slidesPerView={computedConfigs.gamesPerRow}
          />
        </div>
      );
    }

    return res;
  }, [data, gamesCount]);

  return (
    <Suspense fallback={<CasinoGamesByCategorySkeleton />}>
      <div
        className={cc([
          {
            'casinoGame__carousel-wrapper': !isMobile(),
            'casinoGame__carousel-wrapper-mobile': isMobile(),
            'casinoGame__carousel-without-arrows':
              isMobile() && !computedConfigs.arrowsEnabled,
            casinoGame__with__title: computedConfigs?.gameTitle
          }
        ])}
        ref={containerRef}
      >
        <SwiperSlider
          bodyArrows={isMobile() ? computedConfigs.arrowsEnabled : true}
          activeSlide={0}
          slidesPerView={computedConfigs.gamesPerRow}
          disableSwiping={!isMobile() || computedConfigs.arrowsEnabled}
          alwaysShowArrows={isMobile()}
          spaceBetween={8}
          slideOnHover={computedConfigs.nextSlideOnHover && !isMobile()}
          fetchOnProgress={{
            point: 0.7,
            callback: () => {
              debouncedLoadMoreGames();
            }
          }}
          drawableTabs={drawableData}
          notToWatchIsEnd={true}
        />
      </div>
    </Suspense>
  );
};

export const CasinoGamesCarousel = memo(_CasinoGamesCarousel);
